import { useState } from 'react';
import Collapsible from 'react-collapsible';
import { Link } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import '../styles/CookiesSettingsPopup.scss';

type CookiesSettingsPopupProps = {
  onAcceptAll: (e: React.MouseEvent<HTMLElement>) => void;
  onConfirmMyChoices: (optionsData: {
    performance: boolean;
    targeting: boolean;
  }) => void;
};

const CookiesSettingsPopup: React.FC<CookiesSettingsPopupProps> = (props) => {
  const [cookies] = useCookies(['performance_cookies', 'targeting_cookies']);

  const [optionsData, setOptionsData] = useState({
    performance: cookies.performance_cookies === 'yes',
    targeting: cookies.targeting_cookies === 'yes',
  });

  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { name } = event.currentTarget;

    setOptionsData({
      ...optionsData,
      [name]: event.currentTarget.checked,
    });
  };

  const onConfirmMyChoices = () => {
    props.onConfirmMyChoices(optionsData);
  };

  return (
    <div className="settings-container">
      <h1 className="settings-title">COOKIE USAGE</h1>
      <p className="settings-text">
        When you visit any website, it may store or retrieve information on your
        browser, mostly in the form of cookies. This information might be about
        you, your preferences or your device and is mostly used to make the site
        work as you expect it to. The information does not usually directly
        identify you, but it can give you a more personalized web experience.
        Because we respect your right to privacy, you can choose not to allow
        some types of cookies. Click on the different category headings to find
        out more and change our default settings. However, blocking some types
        of cookies may impact your experience of the site and the services we
        are able to offer. <br />
        <Link to="/privacy-notice">Our privacy policy</Link>
      </p>

      <div className="options-container">
        <div className="row">
          <input
            type="checkbox"
            id="necessary_cookies"
            name="necessary_cookies"
            checked={true}
            disabled
          />
          <label
            htmlFor="necessary_cookies"
            className="options-checkbox"
          ></label>
          <Collapsible trigger="Strictly necessary cookies">
            <p>
              These cookies are necessary for the website to function and cannot
              be switched off in our systems. They are usually only set in
              response to actions made by you which amount to a request for
              services, such as setting your privacy preferences, logging in or
              filling in forms. You can set your browser to block or alert you
              about these cookies, but some parts of the site will not then
              work. These cookies do not store any personally identifiable
              information.
            </p>
          </Collapsible>
        </div>

        <div className="row">
          <input
            type="checkbox"
            id="performance"
            name="performance"
            checked={optionsData.performance}
            onChange={handleChange}
          />
          <label htmlFor="performance" className="options-checkbox"></label>
          <Collapsible trigger=" Performance cookies">
            <p>
              These cookies allow us to count visits and traffic sources so we
              can measure and improve the performance of our site. They help us
              to know which pages are the most and least popular and see how
              visitors move around the site. All information these cookies
              collect is aggregated and therefore anonymous. If you do not allow
              these cookies we will not know when you have visited our site, and
              will not be able to monitor its performance.
            </p>
          </Collapsible>
        </div>

        <div className="row">
          <input
            type="checkbox"
            id="targeting"
            name="targeting"
            checked={optionsData.targeting}
            onChange={handleChange}
          />
          <label htmlFor="targeting" className="options-checkbox"></label>
          <Collapsible trigger="Targeting cookies">
            <p>
              These cookies may be set through our site by our advertising
              partners. They may be used by those companies to build a profile
              of your interests and show you relevant adverts on other sites.
              They do not store directly personal information, but are based on
              uniquely identifying your browser and internet device. If you do
              not allow these cookies, you will experience less targeted
              advertising.
            </p>
          </Collapsible>
        </div>
      </div>

      <div className="settings-btn-container">
        <button className="accept-all" onClick={props.onAcceptAll}>
          Accept all
        </button>
        <button className="reject-all" onClick={onConfirmMyChoices}>
          Confirm my choices
        </button>
      </div>
    </div>
  );
};

export default CookiesSettingsPopup;
